import { Badge } from '@radix-ui/themes';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import { EmptyState } from '@/components/ui/empty-state';
import { useGetAlertsQuery } from '@/services';
import { generateAlertMessage } from '@/utils/alert-utils';
import { Link } from '@tanstack/react-router';

// Legacy interface for backward compatibility
export interface Alert {
  id: string;
  message: string;
  severity: 'critical' | 'warning' | 'info';
  timestamp: string;
  device: string;
}

interface AlertsListProps {
  alerts?: Alert[];
  deviceId?: string;
  limit?: number;
  showAll?: boolean;
}

export function AlertsList({ alerts: providedAlerts, deviceId, limit = 5, showAll = false }: AlertsListProps) {
  // Use the API if no alerts are provided
  const { data: alertsData, isLoading } = useGetAlertsQuery(
    {
      deviceId,
      limit: limit.toString(),
      includeRule: 'true',
      sortBy: 'triggered_at',
      sortDir: 'desc',
    },
    {
      // Skip if alerts are provided or no deviceId is available
      skip: !!providedAlerts || (!deviceId && !showAll),
    },
  );

  // Use provided alerts or API data
  const alerts = providedAlerts || alertsData?.data?.items || [];

  if (isLoading) {
    return <AlertsListSkeleton count={limit} />;
  }

  if (alerts.length === 0) {
    return (
      <EmptyState
        icon={CheckCircle}
        title="No alerts"
        description="All systems are operating normally"
        iconClassName="text-green-500 bg-green-50 dark:bg-green-900/20"
      />
    );
  }

  return (
    <ScrollArea className="h-[400px] pr-4">
      <div className="space-y-4">
        {alerts.map((alert) => {
          // Handle both legacy and API alert formats
          const isLegacyFormat = 'message' in alert;

          // Format timestamp based on when it occurred
          let formattedTime;
          const timestamp = isLegacyFormat ? new Date(alert.timestamp) : new Date(alert.triggered_at);

          if (isToday(timestamp)) {
            formattedTime = `Today, ${format(timestamp, 'h:mm a')}`;
          } else if (isYesterday(timestamp)) {
            formattedTime = `Yesterday, ${format(timestamp, 'h:mm a')}`;
          } else {
            formattedTime = format(timestamp, 'MMM d, h:mm a');
          }

          // For legacy format, directly use fields, for API format use mapping
          const severity = isLegacyFormat
            ? alert.severity
            : (alert.severity as 'critical' | 'high' | 'medium' | 'low' | 'info');

          const deviceName = isLegacyFormat ? alert.device : alert.device?.name || 'Unknown device';

          const deviceId = !isLegacyFormat ? alert.device?.id : undefined;

          return (
            <Card key={alert.id} className="p-4 hover:bg-muted/10 transition-colors">
              <div className="flex items-center justify-between">
                <Badge
                  color={
                    severity === 'critical' || severity === 'high'
                      ? 'red'
                      : severity === 'warning' || severity === 'medium'
                        ? 'yellow'
                        : 'blue'
                  }
                >
                  {severity}
                </Badge>
                <span className="text-sm text-muted-foreground">{formattedTime}</span>
              </div>
              <div className="mt-2">
                <p className="font-medium">{isLegacyFormat ? alert.message : generateAlertMessage(alert)}</p>
                {deviceId ? (
                  <Link
                    to="/dashboard/devices/$deviceId"
                    params={{ deviceId }}
                    className="text-sm text-muted-foreground hover:text-primary mt-1 block"
                  >
                    Device: {deviceName}
                  </Link>
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">Device: {deviceName}</p>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </ScrollArea>
  );
}

function AlertsListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-4">
      {Array(count)
        .fill(0)
        .map((_, i) => (
          <Card key={i} className="p-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="mt-2">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-4 w-40 mt-2" />
            </div>
          </Card>
        ))}
    </div>
  );
}
